import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Search, ArrowUpRight, Users, AlertTriangle, Settings } from "lucide-react";

// ------------------------------------------------------------------ Create dialog
function CreateDialog({ open, onClose, lines, statuses, onCreated }) {
  const empty = { name: "", domain: "", phone: "", email: "", industry: "", address_city: "", address_state: "", relationship_status: "New", business_line_ids: [] };
  const [form, setForm] = useState(empty);
  const [dupes, setDupes] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) { setForm(empty); setDupes([]); } }, [open]);

  const toggleLine = (id) => setForm((f) => ({
    ...f, business_line_ids: f.business_line_ids.includes(id) ? f.business_line_ids.filter((x) => x !== id) : [...f.business_line_ids, id],
  }));

  const checkDupes = async () => {
    if (!form.name.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/boi/relationships/check-duplicates", form);
      setDupes(data.duplicates || []);
      if (!data.count) toast.success("No duplicates found — good to create.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setChecking(false);
  };

  const create = async (force) => {
    if (!form.name.trim()) { toast.error("Relationship name is required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/boi/relationships", { ...form, force });
      toast.success("Business relationship created.");
      onCreated(data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicates) {
        setDupes(d.duplicates);
        toast.warning("Possible duplicates found — review below, then Create anyway.");
      } else { toast.error(formatApiError(d)); }
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="br-create-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy">New Business Relationship</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Business / Owner Name *"><Input data-testid="br-field-name" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={checkDupes} placeholder="ABC Manufacturing" /></Field>
            <Field label="Relationship Status">
              <select data-testid="br-field-status" className="w-full h-9 border border-border rounded-sm px-2 text-sm" value={form.relationship_status} onChange={(e) => set("relationship_status", e.target.value)}>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Web Domain"><Input data-testid="br-field-domain" value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder="abcmfg.com" /></Field>
            <Field label="Phone"><Input data-testid="br-field-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email"><Input data-testid="br-field-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Industry"><Input value={form.industry} onChange={(e) => set("industry", e.target.value)} /></Field>
            <Field label="City"><Input value={form.address_city} onChange={(e) => set("address_city", e.target.value)} /></Field>
            <Field label="State"><Input value={form.address_state} onChange={(e) => set("address_state", e.target.value)} /></Field>
          </div>
          <div>
            <div className="text-xs uppercase text-mutedsea tracking-wide mb-1.5">Business Lines (carrier-neutral)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="br-lines-picker">
              {lines.map((l) => (
                <button key={l.id} type="button" onClick={() => toggleLine(l.id)} data-testid={`br-line-${l.id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.business_line_ids.includes(l.id) ? "bg-navy text-white border-navy" : "bg-white text-atlantic border-border hover:bg-ice"}`}>
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          {dupes.length > 0 && (
            <div className="border border-seaamber/40 bg-seaamber/5 rounded-sm p-3" data-testid="br-dupes">
              <div className="flex items-center gap-1.5 text-seaamber text-sm font-medium mb-1"><AlertTriangle className="w-4 h-4" /> {dupes.length} possible duplicate(s)</div>
              <ul className="text-xs text-slate space-y-1">
                {dupes.map((d, i) => <li key={i}>· <span className="font-medium">{d.name}</span> — {d.type.replace("_", " ")} · matched on {d.match_on} ({d.confidence}%)</li>)}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={checkDupes} disabled={checking} data-testid="br-check-dupes">{checking ? "Checking…" : "Check Duplicates"}</Button>
          <Button onClick={() => create(false)} disabled={saving} data-testid="br-create-submit" className="bg-navy hover:bg-atlantic">
            {dupes.length ? "Create Anyway" : (saving ? "Creating…" : "Create Relationship")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs uppercase text-mutedsea tracking-wide">{label}</span><div className="mt-1">{children}</div></label>
);

// ------------------------------------------------------------------ Promote tab
function PromoteTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/boi/promote/candidates", { params: { q } }); setItems(data.items); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const promote = async (party_id) => {
    setBusy(party_id);
    try {
      const { data } = await api.post("/boi/promote", { party_id });
      toast.success(`Promoted — ${data.contacts_copied} contact(s) carried over.`);
      nav(`/relationships/${data.relationship.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-sm"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-mutedsea" /><Input className="pl-8" placeholder="Search intelligence prospects…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="promote-search" /></div>
      </div>
      <p className="text-sm text-mutedsea mb-3">Existing Endurance Intelligence prospects not yet promoted. Promoting creates a master relationship and carries over decision-makers — the source record is never destroyed.</p>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Name", "Status", "Industry", "Score", "Location", ""].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.party_id} className="border-b border-border last:border-0 hover:bg-ice" data-testid={`promote-row-${c.party_id}`}>
                <td className="px-3 py-2 font-medium text-navy">{c.name}</td>
                <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{c.relationship}</Badge></td>
                <td className="px-3 py-2 text-xs text-mutedsea">{c.industry || "—"}</td>
                <td className="px-3 py-2 text-xs">{c.score ?? "—"} {c.priority_label ? `· ${c.priority_label}` : ""}</td>
                <td className="px-3 py-2 text-xs text-mutedsea">{[c.address_city, c.address_state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" disabled={busy === c.party_id} onClick={() => promote(c.party_id)} data-testid={`promote-btn-${c.party_id}`}><ArrowUpRight className="w-3.5 h-3.5 mr-1" />{busy === c.party_id ? "Promoting…" : "Promote"}</Button></td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-mutedsea text-sm">No unpromoted intelligence prospects.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Business lines tab (admin)
function LinesTab() {
  const [lines, setLines] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Property & Casualty");
  const [regulated, setRegulated] = useState(false);

  const load = async () => { const { data } = await api.get("/boi/business-lines", { params: { include_inactive: true } }); setLines(data.items); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/boi/business-lines", { name, category, is_regulated_financial: regulated }); toast.success("Business line added."); setName(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => { try { await api.post(`/boi/business-lines/${id}/toggle`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <p className="text-sm text-mutedsea mb-3">Carrier-neutral business lines. Deactivating keeps history intact; it only hides the line from new pickers.</p>
      <div className="flex flex-wrap items-end gap-2 mb-4 bg-white border border-border rounded-sm p-3">
        <Field label="New Line Name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="line-name" placeholder="e.g. Captive & Alternative Risk" /></Field>
        <Field label="Category">
          <select className="h-9 border border-border rounded-sm px-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="line-category">
            {["Property & Casualty", "Benefits", "Financial Services", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-1.5 text-sm h-9"><input type="checkbox" checked={regulated} onChange={(e) => setRegulated(e.target.checked)} data-testid="line-regulated" /> Regulated Financial</label>
        <Button onClick={add} className="bg-navy hover:bg-atlantic" data-testid="line-add"><Plus className="w-4 h-4 mr-1" />Add Line</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {lines.map((l) => (
          <div key={l.id} className={`border rounded-sm p-3 flex items-center justify-between ${l.active ? "bg-white border-border" : "bg-cloud border-border opacity-70"}`} data-testid={`line-${l.id}`}>
            <div>
              <div className="font-medium text-navy text-sm">{l.name}</div>
              <div className="text-xs text-mutedsea">{l.category}{l.is_regulated_financial ? " · Regulated" : ""}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggle(l.id)} data-testid={`line-toggle-${l.id}`}>{l.active ? "Deactivate" : "Activate"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Score Config tab (admin)
function ScoreConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get("/boi/score-config"); setCfg(data); if (!sel && data.rules[0]) setSel(data.rules[0].business_line_id); };
  useEffect(() => { load(); }, []);
  if (!cfg) return <p className="text-sm text-mutedsea">Loading configuration…</p>;
  const rule = cfg.rules.find((r) => r.business_line_id === sel);

  const setFactor = (key, patch) => {
    const factors = rule.factors.map((f) => f.key === key ? { ...f, ...patch } : f);
    setCfg((c) => ({ ...c, rules: c.rules.map((r) => r.business_line_id === sel ? { ...r, factors } : r) }));
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/boi/score-config/${sel}`, { factors: rule.factors }); toast.success(`Saved as v${data.version} — recalculation queued.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const bulk = async () => { try { const { data } = await api.post("/boi/recalc/bulk", { scope: "all", reason: "manual all-recalc" }); toast.success("Bulk recalculation queued."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="score-config-tab">
      <div className="flex items-center gap-2 mb-3">
        <select className="h-9 border border-border rounded-sm px-2 text-sm" value={sel || ""} onChange={(e) => setSel(e.target.value)} data-testid="config-line-select">
          {cfg.rules.map((r) => <option key={r.business_line_id} value={r.business_line_id}>{r.line_name} (v{r.version})</option>)}
        </select>
        <Button variant="outline" onClick={bulk} data-testid="bulk-recalc-btn"><RefreshCwIcon />Recalculate All</Button>
        <span className="text-xs text-mutedsea ml-auto">Thresholds: {cfg.thresholds.map((b) => `${b.label}≥${b.min}`).join(" · ")}</span>
      </div>
      {rule && (
        <div className="bg-white border border-border rounded-sm p-4">
          <p className="text-sm text-mutedsea mb-3">Deterministic weights for <span className="font-medium text-navy">{rule.line_name}</span>. Max-points = a factor's maximum contribution. Missing data never adds points (it lowers data confidence).</p>
          <div className="space-y-2">
            {rule.factors.map((f) => (
              <div key={f.key} className="flex items-center gap-3 border-b border-border py-2" data-testid={`config-factor-${f.key}`}>
                <input type="checkbox" checked={f.enabled} onChange={(e) => setFactor(f.key, { enabled: e.target.checked })} data-testid={`config-enable-${f.key}`} />
                <span className="flex-1 text-sm text-navy">{f.label}</span>
                <label className="text-xs text-mutedsea">max</label>
                <Input type="number" className="w-20 h-8" value={f.max_points} onChange={(e) => setFactor(f.key, { max_points: Number(e.target.value) })} data-testid={`config-weight-${f.key}`} />
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-mutedsea">Total max points: {rule.factors.filter((f) => f.enabled).reduce((a, f) => a + Number(f.max_points || 0), 0)}</div>
          <Button onClick={save} disabled={busy} className="bg-navy hover:bg-atlantic mt-3" data-testid="config-save">Save Rule & Queue Recalc</Button>
        </div>
      )}
    </div>
  );
}
const RefreshCwIcon = () => <svg className="w-4 h-4 mr-1 inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>;

// ------------------------------------------------------------------ Main page
export default function BusinessRelationships() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ lines: [], statuses: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [minScore, setMinScore] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.get("/boi/business-lines").then(({ data }) => setMeta({ lines: data.items, statuses: data.statuses })); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, relationship_status: statusFilter };
      if (minScore) params.min_highest_score = Number(minScore);
      const { data } = await api.get("/boi/relationships", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [q, statusFilter, minScore]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="business-relationships-page">
      <PageHeader title="Business Owner Intelligence" subtitle="Master business relationships that sit above individual insurance & financial opportunities.">
        {can.write && <Button onClick={() => setShowCreate(true)} className="bg-navy hover:bg-atlantic" data-testid="br-new-btn"><Plus className="w-4 h-4 mr-1" />New Relationship</Button>}
      </PageHeader>

      <Tabs defaultValue="relationships">
        <TabsList data-testid="boi-tabs">
          <TabsTrigger value="relationships" data-testid="tab-relationships"><Building2 className="w-4 h-4 mr-1" />Relationships</TabsTrigger>
          {can.write && <TabsTrigger value="promote" data-testid="tab-promote"><ArrowUpRight className="w-4 h-4 mr-1" />Promote from Intelligence</TabsTrigger>}
          {can.admin && <TabsTrigger value="lines" data-testid="tab-lines"><Settings className="w-4 h-4 mr-1" />Business Lines</TabsTrigger>}
          {can.admin && <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-4 h-4 mr-1" />Score Config</TabsTrigger>}
        </TabsList>

        <TabsContent value="relationships" className="mt-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 max-w-sm"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-mutedsea" /><Input className="pl-8" placeholder="Search relationships…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="br-search" /></div>
            <select className="h-9 border border-border rounded-sm px-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="br-status-filter">
              <option value="">All Statuses</option>
              {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="h-9 border border-border rounded-sm px-2 text-sm" value={minScore} onChange={(e) => setMinScore(e.target.value)} data-testid="br-score-filter">
              <option value="">Any Score</option>
              <option value="75">Top Score ≥ 75 (High)</option>
              <option value="60">Top Score ≥ 60 (Good)</option>
              <option value="40">Top Score ≥ 40</option>
            </select>
            <span className="text-xs text-mutedsea ml-auto">{total} relationship{total === 1 ? "" : "s"}</span>
          </div>
          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-cloud border-b border-border">{["Name", "Status", "Best Next Opportunity", "Top Score", "Confidence", "Best Next Action", "Contacts"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/relationships/${r.id}`)} className="border-b border-border last:border-0 hover:bg-ice cursor-pointer" data-testid={`br-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-navy">{r.name}{r.do_not_contact && <span className="ml-1.5 text-xs text-seared">· DNC</span>}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{r.relationship_status}</Badge></td>
                    <td className="px-3 py-2 text-xs text-navy">{r.best_next_opportunity?.business_line_name || "—"}{r.best_next_opportunity ? <span className="text-mutedsea"> ({r.best_next_opportunity.score})</span> : ""}</td>
                    <td className="px-3 py-2"><span className="font-serif text-lg text-navy">{r.highest_score ?? 0}</span></td>
                    <td className="px-3 py-2 text-xs text-mutedsea">{r.data_confidence_overall ?? 0}%</td>
                    <td className="px-3 py-2 text-xs text-atlantic">{r.best_next_action_label || "—"}</td>
                    <td className="px-3 py-2 text-xs"><span className="inline-flex items-center gap-1 text-mutedsea"><Users className="w-3.5 h-3.5" />{r.contact_count ?? 0}</span></td>
                  </tr>
                ))}
                {!loading && items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-mutedsea text-sm" data-testid="br-empty">No business relationships yet. Create one or promote an intelligence prospect.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {can.write && <TabsContent value="promote" className="mt-4"><PromoteTab /></TabsContent>}
        {can.admin && <TabsContent value="lines" className="mt-4"><LinesTab /></TabsContent>}
        {can.admin && <TabsContent value="config" className="mt-4"><ScoreConfigTab /></TabsContent>}
      </Tabs>

      <CreateDialog open={showCreate} onClose={() => setShowCreate(false)} lines={meta.lines} statuses={meta.statuses} onCreated={() => load()} />
    </div>
  );
}
